import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Image, Layers, Star, Wallet, Gavel, Users,
  Sun, Moon, LogOut, Shield, Menu, KeyRound, Trophy, Receipt,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import NotificationBell from "@/components/NotificationBell";
import { TrialBanner } from "@/components/TrialBanner";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import arcLogo from "@/assets/arc-logo.png";

const links = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/gallery", label: "Gallery", icon: Image },
  { to: "/holdings", label: "My Arts", icon: Layers },
  { to: "/review", label: "Daily Review", icon: Star },
  { to: "/auctions", label: "Auctions", icon: Gavel },
  { to: "/wallets", label: "Wallets", icon: Wallet },
  { to: "/transactions", label: "Transactions", icon: Receipt },
  { to: "/referrals", label: "Refer & Earn", icon: Users },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy },
  { to: "/security", label: "Security", icon: KeyRound },
];

const bottomLinks = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard },
  { to: "/gallery", label: "Gallery", icon: Image },
  { to: "/holdings", label: "My Arts", icon: Layers },
  { to: "/review", label: "Review", icon: Star },
  { to: "/auctions", label: "Auctions", icon: Gavel },
  { to: "/wallets", label: "Wallets", icon: Wallet },
  { to: "/referrals", label: "Refer", icon: Users },
];

export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle"
      className="h-10 w-10 grid place-items-center rounded-full hover:bg-secondary transition-colors"
      aria-label="Toggle theme"
    >
      {theme === "dark" ? <Sun className="h-5 w-5 text-gold" /> : <Moon className="h-5 w-5" />}
    </button>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    setOpen(false);
    await logout();
    nav("/", { replace: true });
  };

  // Auto screen-lock: log out after 15 minutes of inactivity.
  useEffect(() => {
    if (!user) return undefined;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        await logout();
        toast.info("You were signed out after 15 minutes of inactivity. Please sign in again.");
        nav("/login", { replace: true });
      }, 15 * 60 * 1000);
    };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(timer); events.forEach((e) => window.removeEventListener(e, reset)); };
  }, [user, logout, nav]);

  const go = (to) => {
    setOpen(false);
    nav(to);
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <header className="sticky top-0 z-40 glass-nav bg-background/70 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOpen(true)}
              data-testid="hamburger-btn"
              aria-label="Open menu"
              className="h-10 w-10 grid place-items-center rounded-full hover:bg-secondary transition-colors"
            >
              <Menu className="h-5 w-5" />
            </button>
            <NavLink to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
              <img src={arcLogo} alt="Art Review Club" className="h-9 w-9 rounded-xl object-contain" />
              <span className="font-heading text-xl font-bold tracking-tight hidden sm:block">Art Review Club</span>
            </NavLink>
          </div>

          <div className="flex items-center gap-1">
            <NotificationBell />
            <ThemeToggle />
            <button onClick={doLogout} data-testid="logout-btn" className="h-10 w-10 grid place-items-center rounded-full hover:bg-secondary transition-colors" aria-label="Log out">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Full navigation drawer (all breakpoints) */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[300px] p-0 flex flex-col" data-testid="nav-drawer">
          <SheetHeader className="p-6 border-b border-border">
            <SheetTitle className="flex items-center gap-2 text-left">
              <img src={arcLogo} alt="Art Review Club" className="h-9 w-9 rounded-xl object-contain" />
              <span className="font-heading text-lg font-bold">Art Review Club</span>
            </SheetTitle>
          </SheetHeader>

          {user && (
            <div className="px-6 py-4 border-b border-border">
              <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground font-bold">Member</p>
              <p className="font-semibold truncate">{user.username}</p>
              <p className="text-xs text-muted-foreground">{user.member_id}</p>
            </div>
          )}

          <nav className="flex-1 overflow-y-auto p-3 space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`drawer-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 h-12 rounded-2xl text-sm font-medium transition-colors ${
                    isActive ? "navy-gradient text-white" : "hover:bg-secondary text-foreground"
                  }`
                }
              >
                <l.icon className="h-5 w-5" />
                {l.label}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                onClick={() => setOpen(false)}
                data-testid="drawer-admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 h-12 rounded-2xl text-sm font-medium transition-colors ${
                    isActive ? "navy-gradient text-white" : "hover:bg-secondary text-foreground"
                  }`
                }
              >
                <Shield className="h-5 w-5 text-gold" />
                Admin Panel
              </NavLink>
            )}
          </nav>

          <div className="p-3 border-t border-border">
            <button
              onClick={doLogout}
              data-testid="drawer-logout"
              className="flex items-center gap-3 px-4 h-12 w-full rounded-2xl text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
            >
              <LogOut className="h-5 w-5" /> Log Out
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <main className="max-w-7xl mx-auto px-4 py-6 md:py-10"><TrialBanner />{children}</main>

      {/* Bottom tab bar */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 glass-nav bg-background/90 border-t border-border">
        <div className="grid grid-cols-7">
          {bottomLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`tab-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-2 text-[9px] font-medium transition-colors ${
                  isActive ? "text-primary dark:text-white" : "text-muted-foreground"
                }`
              }
            >
              <l.icon className="h-[18px] w-[18px]" />
              {l.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
